import React, { type ReactNode } from "react";
import {
  Box,
  Flex,
  Button as ChakraButton,
  type ButtonProps as ChakraButtonProps,
  type LinkProps,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "ui/icons";
import Link from "next/link";

type BaseButtonProps = {
  /**
   * The URL the link points to.
   * If `href` is not provided, the link will be rendered as a button.
   */
  href?: string;

  /**
   * Button text.
   */
  children: ReactNode;

  /**
   * Button variant, which determines the purpose the button is used for and its appearance.
   *
   * @default "primary"
   */
  variant?: "primary" | "secondary" | "tertiary";

  /**
   * Determines the height of the button.
   */
  size?: "sm" | "md" | "lg";
};

export type DirectionalButtonProps = BaseButtonProps &
  Omit<ChakraButtonProps, "children" | "variant" | "size"> &
  Omit<LinkProps, "href">;

export function DirectionalButton({
  "aria-label": ariaLabel,
  variant = "primary",
  size = "md",
  children,
  ...props
}: DirectionalButtonProps) {
  const iconSize = `var(--chakra-sizes-icon-${size})`;
  let bgColor = "brand.accent.primary";
  if (variant === "secondary") {
    bgColor = "brand.backgroundSecondary.light";
  } else if (variant === "tertiary") {
    bgColor = "brand.background.light";
  }

  // Keep the box size consistent with the icon size on both default and hover states
  const boxSize = `calc(${iconSize} + 8px)`;
  const hoverBoxSize = `calc(${iconSize} * 2 + 4px)`;

  function RightIcon() {
    return (
      <Box pos="relative" boxSize={boxSize}>
        <Flex
          align="center"
          bg={bgColor}
          borderRadius="full"
          justify="center"
          pos="absolute"
          inset={0}
          width="100%"
          transition="width 0.15s ease"
          _groupHover={{ width: hoverBoxSize }}
          _groupActive={{ width: hoverBoxSize }}
        >
          <ChevronRightIcon boxSize={iconSize} color="brand.accent.compliment" />
        </Flex>
      </Box>
    );
  }

  return (
    <ChakraButton
      as={props.href ? Link : undefined}
      aria-label={ariaLabel ?? children?.toString()}
      className="group"
      variant="directional"
      size={size}
      iconSpacing={3}
      rightIcon={<RightIcon />}
      {...props}
    >
      {children}
    </ChakraButton>
  );
}
